import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Gift, ArrowRight, Check, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Plans used for the $1 upsell plan-selection step
const PLANS = [
  { id: "starter",     name: "Starter",          price: 29.99, classes: 3,  extraClassPrice: 9.99 },
  { id: "performance", name: "Performance",       price: 59.99, classes: 6,  extraClassPrice: 4.99 },
  { id: "elite",       name: "Elite Unlimited",   price: 79.99, classes: -1, extraClassPrice: 0    },
];

export const LeadPopup = ({ isOpen, onClose, onComplete, dynamicOffer = null, dynamicUpsell = null }) => {
  // All content is driven from dynamicOffer and dynamicUpsell — no hardcoded copy anywhere.
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [leadData, setLeadData] = useState({ first_name: "", last_name: "", phone: "" });
  const [leadId, setLeadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("elite");

  // Reset state each time popup opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setLeadData({ first_name: "", last_name: "", phone: "" });
      setLeadId(null);
    }
  }, [isOpen]);

  // Safety: if no offer is configured, render nothing
  if (!dynamicOffer) return null;

  const isUnlimited = dynamicOffer.reward_type === "unlimited_time";
  const hasUpsell   = !!dynamicUpsell;
  const OfferIcon   = isUnlimited ? Gift : Calendar;
  const ctaBtnText  = isUnlimited ? "Claim My Free Week" : "Book My Free Class";
  const reservedMsg = isUnlimited ? "Free Week Reserved!" : "Free Class Reserved!";
  const keepLabel   = isUnlimited ? "Free Week" : "Free Class";

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadData.first_name || !leadData.last_name || !leadData.phone) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        ...leadData,
        intro_offer_id: dynamicOffer.id || null,
      });
      setLeadId(response.data.id);
      if (window.gtag) window.gtag('event', 'generate_lead', { value: 0, currency: 'USD', lead_type: 'popup_signup' });
      if (window.fbq) window.fbq('track', 'Lead', { content_name: 'Popup Signup' });

      if (isUnlimited) {
        await axios.put(`${API}/leads/${response.data.id}/claim-free-week`);
        if (window.fbq) window.fbq("track", "Lead", { content_name: "Free 1-Week Pass", content_category: "Gym Offer" });
        toast.success(`You got it, ${leadData.first_name}!`);
      } else {
        toast.success(`Thanks ${leadData.first_name}!`);
      }

      if (hasUpsell) {
        setStep(2);
      } else {
        onComplete?.();
        navigate("/register", {
          state: {
            leadData,
            leadId: response.data.id,
            ...(isUnlimited ? { claimedFreeWeek: true } : { freeClass: true }),
          },
        });
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDollarOffer = async () => {
    if (leadId) {
      try { await axios.put(`${API}/leads/${leadId}/accept-dollar-offer`); } catch {}
    }
    if (window.fbq) window.fbq("track", "Lead", { content_name: "$1 First Month", content_category: "Gym Offer" });
    toast.success("Great choice! Complete registration to get your $1 first month.");
    onComplete?.();
    navigate("/register", { state: { leadData, leadId, dollarOffer: true, selectedPlan } });
  };

  const handleKeepOriginalOffer = () => {
    if (isUnlimited) {
      toast.success("Free week claimed! Complete registration to get started.");
      onComplete?.();
      navigate("/register", { state: { leadData, leadId, claimedFreeWeek: true } });
    } else {
      if (window.fbq) window.fbq("track", "Lead", { content_name: "First Class Free", content_category: "Gym Offer" });
      toast.success("Free class reserved! Complete registration to book.");
      onComplete?.();
      navigate("/register", { state: { leadData, leadId, freeClass: true } });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border border-[#FFB612]/30 max-w-md p-0 overflow-hidden max-h-[92svh] overflow-y-auto">
        <DialogTitle className="sr-only">NBC Boxing Club Offer</DialogTitle>
        <DialogDescription className="sr-only">Special offer for new members</DialogDescription>

        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white/50 hover:text-white z-10"
          data-testid="popup-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Step 1 — Lead Capture */}
        {step === 1 && (
          <div className="p-5">
            <div className="h-1 w-16 bg-[#FFB612] mb-4" />
            <div className="flex items-center gap-2 mb-2">
              <OfferIcon className="w-6 h-6 text-[#FFB612] flex-shrink-0" />
              <h2 className="text-lg font-bold uppercase text-white">{dynamicOffer.name}</h2>
            </div>
            <p className="text-[#FFB612] font-bold text-sm mb-4">{dynamicOffer.reward_value}</p>

            <form onSubmit={handleLeadSubmit} className="space-y-3">
              <Input
                type="text"
                placeholder="First Name *"
                value={leadData.first_name}
                onChange={(e) => setLeadData({ ...leadData, first_name: e.target.value })}
                className="bg-[#050505] border-white/20 h-11 text-white placeholder:text-white/40"
                data-testid="lead-first-name-input"
                required
              />
              <Input
                type="text"
                placeholder="Last Name *"
                value={leadData.last_name}
                onChange={(e) => setLeadData({ ...leadData, last_name: e.target.value })}
                className="bg-[#050505] border-white/20 h-11 text-white placeholder:text-white/40"
                data-testid="lead-last-name-input"
                required
              />
              <Input
                type="tel"
                placeholder="Phone Number *"
                value={leadData.phone}
                onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                className="bg-[#050505] border-white/20 h-11 text-white placeholder:text-white/40"
                data-testid="lead-phone-input"
                required
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-11"
                data-testid="lead-submit-btn"
              >
                {loading ? "Processing..." : ctaBtnText}
              </Button>
            </form>

            <p className="text-xs text-white/40 mt-3 text-center">
              This is a one-time offer. By submitting, you agree to receive SMS updates.
            </p>
          </div>
        )}

        {/* Step 2 — Upsell */}
        {step === 2 && dynamicUpsell && (
          <div className="p-5">
            <div className="bg-green-500/10 border border-green-500/30 p-2 mb-3 text-center">
              <p className="text-green-400 font-bold text-sm flex items-center justify-center gap-1">
                <Check className="w-4 h-4" />
                {reservedMsg}
              </p>
            </div>

            <div className="bg-[#FFB612]/10 border border-[#FFB612]/30 p-1.5 mb-3 text-center">
              <p className="text-xs text-[#FFB612] uppercase font-bold tracking-wide">One-Time Offer</p>
            </div>

            <h2 className="text-base font-bold uppercase text-center mb-1 text-white">{dynamicUpsell.name}</h2>
            {dynamicUpsell.description && (
              <p className="text-white text-sm text-center mb-3">{dynamicUpsell.description}</p>
            )}
            <div className="text-center mb-3">
              <span className="text-4xl font-black text-[#FFB612] font-mono">${Number(dynamicUpsell.price).toFixed(2)}</span>
              <span className="text-base text-white ml-2">First Month</span>
            </div>

            {/* Plan selector */}
            <div className="space-y-1.5 mb-3">
              {PLANS.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`w-full p-2.5 border text-left transition-all ${
                    selectedPlan === plan.id
                      ? "border-[#FFB612] bg-[#FFB612]/10"
                      : "border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`offer-plan-${plan.id}`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-sm text-white">{plan.name}</p>
                      <p className="text-xs text-white/60">
                        {plan.classes === -1
                          ? "Unlimited classes"
                          : `${plan.classes} classes/mo + $${plan.extraClassPrice} extra`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#FFB612] font-mono font-bold text-sm">${plan.price}/mo</p>
                      <p className="text-xs text-white/40">starting month 2</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleAcceptDollarOffer}
                className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-10"
                data-testid="accept-dollar-offer-btn"
              >
                {dynamicUpsell.cta_text || "Claim This Offer"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={handleKeepOriginalOffer}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10 uppercase tracking-wide h-9 text-sm"
                data-testid="decline-offer-btn"
              >
                No Thanks, Keep My {keepLabel}
              </Button>
            </div>

            <p className="text-xs text-white/40 mt-2 text-center">
              No risk. No contracts. Cancel anytime — just text Ron at (614) 547-6096.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
